import CGAppreciate from './GameSystemData/CGData/CGAppreciate'
import BGMAppreciate from './GameSystemData/BGMData/BGMAppreciate'
import SaveLoadData from './GameSystemData/SaveLoadData'
import TalkMessageData from './GameSystemData/MessageData/TalkMessageData'
import GameTitleData from './GameSystemData/GameTitleData'
import GameMenuData from './GameSystemData/GameMenuData'
import GameReplay from './GameSystemData/GameReplay'
import GameSettingData from './GameSystemData/GameSettingData'
import TotalSetting from './GameSystemData/TotalSetting'
import FloatButton from './FloatButton/FloatButton'
import BubblePerson from './GameSystemData/BubblePerson'
import BtnList from './GameSystemData/BtnList'
import ModuleId from './DataStructModuleId'
import CustomUI from './CustomUI/CustomUI'
import { FileReader, FileWriter } from '@/platform'
import DataUtils from './DataUtils'

type ReadProgress = (value: number, message: string) => void | Promise<void>

class SystemData {
    public mid = ModuleId.systemData

    /**
     * CG鉴赏
     */
    public CG: CGAppreciate = new CGAppreciate()

    /**
     * BGM鉴赏
     */
    public BGM: BGMAppreciate = new BGMAppreciate()

    /**
     * 存档界面
     */
    public Sldata: SaveLoadData = new SaveLoadData()

    /**
     * 对话界面
     */
    public Talk: TalkMessageData = new TalkMessageData()

    /**
     * 标题11
     */
    public Title: GameTitleData = new GameTitleData()

    /**
     * 游戏菜单
     */
    public GameMenu: GameMenuData = new GameMenuData()

    /**
     *  按钮集合
     */
    public btnList: BtnList = new BtnList()

    /**
     * 回放
     */
    public backPlay: GameReplay = new GameReplay()

    /**
     * 是否显示系统菜单
     */
    public IsShowSystemMenu: boolean = false

    /**
     * 系统菜单
     */
    public SystemMenu: GameSettingData = new GameSettingData() //

    /**
     *  总设信息
     */
    public totalSetting: TotalSetting = new TotalSetting()

    /**
     *  高级ui数据
     */
    public customUI: CustomUI = new CustomUI()

    /**
     *  悬浮按钮
     */
    public floatButton: FloatButton = new FloatButton()

    /**
     *  气泡用人物设置
     */
    public bubblePersion: BubblePerson = new BubblePerson()

    private readStep(reader: FileReader, stage: string, action: () => void): void {
        try {
            action()
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error)
            throw new Error(`${stage}：${message}`)
        }
    }

    private async readAsyncStep(reader: FileReader, stage: string, action: () => Promise<void>): Promise<void> {
        try {
            await action()
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error)
            throw new Error(`${stage}：${message}`)
        }
    }

    async readBin(reader: FileReader, onProgress?: ReadProgress): Promise<void> {
        const notify = async (value: number, message: string) => {
            await onProgress?.(value, message)
        }

        await notify(2, '读取CG鉴赏…')
        this.CG = new CGAppreciate()
        this.readStep(reader, '读取CG鉴赏', () => this.CG.readBin(reader))

        await notify(8, '读取BGM鉴赏…')
        this.BGM = new BGMAppreciate()
        this.readStep(reader, '读取BGM鉴赏', () => this.BGM.readBin(reader))

        await notify(14, '读取存档界面…')
        this.Sldata = new SaveLoadData()
        this.readStep(reader, '读取存档界面', () => this.Sldata.readBin(reader))

        await notify(20, '读取对话界面…')
        this.Talk = new TalkMessageData()
        this.readStep(reader, '读取对话界面', () => this.Talk.readBin(reader))

        await notify(26, '读取标题界面…')
        this.Title = new GameTitleData()
        this.readStep(reader, '读取标题界面', () => this.Title.readBin(reader))

        await notify(32, '读取作品菜单…')
        this.GameMenu = new GameMenuData()
        this.readStep(reader, '读取作品菜单', () => this.GameMenu.readBin(reader))

        await notify(38, '读取按钮集合…')
        this.btnList = new BtnList()
        this.readStep(reader, '读取按钮集合', () => this.btnList.readBin(reader))

        await notify(44, '读取回放数据…')
        this.backPlay = new GameReplay()
        this.readStep(reader, '读取回放数据', () => this.backPlay.readBin(reader))

        this.readStep(reader, '读取系统菜单开关', () => {
            this.IsShowSystemMenu = reader.readBool()
        })

        await notify(56, '读取系统设置…')
        this.SystemMenu = new GameSettingData()
        this.readStep(reader, '读取系统设置', () => this.SystemMenu.readBin(reader))

        await notify(64, '读取总设信息…')
        this.totalSetting = new TotalSetting()
        this.readStep(reader, '读取总设信息', () => this.totalSetting.readBin(reader))

        await notify(72, '读取高级UI…')
        this.customUI = new CustomUI()
        await this.readAsyncStep(reader, '读取高级UI', () => this.customUI.readBin(reader, async (value, message) => {
            await notify(72 + Math.round(value * 0.18), message)
        }))

        await notify(92, '读取悬浮按钮…')
        this.floatButton = new FloatButton()
        this.readStep(reader, '读取悬浮按钮', () => this.floatButton.readBin(reader))

        await notify(98, '读取气泡人物…')
        this.bubblePersion = new BubblePerson()
        this.readStep(reader, '读取气泡人物', () => this.bubblePersion.readBin(reader))
    }

    writeBin(writer: FileWriter): void {
        this.CG.writeBin(writer)
        this.BGM.writeBin(writer)
        this.Sldata.writeBin(writer)
        this.Talk.writeBin(writer)
        this.Title.writeBin(writer)
        this.GameMenu.writeBin(writer)
        this.btnList.writeBin(writer)
        this.backPlay.writeBin(writer)
        writer.writeBool(this.IsShowSystemMenu)
        this.SystemMenu.writeBin(writer)
        this.totalSetting.writeBin(writer)
        this.customUI.writeBin(writer)
        this.floatButton.writeBin(writer)
        this.bubblePersion.writeBin(writer)
    }

    readVersionedOui(reader: FileReader, dataVersion: number): void {
        this.totalSetting.FontName = reader.readString()
        this.totalSetting.FontSize = reader.readInt()
        this.totalSetting.FontColor_MSG = reader.readString()
        this.totalSetting.FontColor_UI = reader.readString()
        this.totalSetting.FontStyle = reader.readInt()
        this.totalSetting.IsTitle = reader.readInt() == 1 ? true : false
        this.totalSetting.StartMapId = reader.readInt()
        this.totalSetting.Ico.readVersionedOui(reader.readObj(reader.readInt()))
        this.totalSetting.Show_ad = reader.readInt()
        this.totalSetting.AuthorWords = reader.readString()
        this.totalSetting.AuthorWordTime = reader.readInt()

        this.SystemMenu.IsAutoText = reader.readInt() ? true : false
        this.IsShowSystemMenu = reader.readInt() ? true : false

        this.totalSetting.EnterSE.readVersionedOui(reader.readObj(reader.readInt()))
        this.totalSetting.MoveSE.readVersionedOui(reader.readObj(reader.readInt()))
        this.totalSetting.CancelSE.readVersionedOui(reader.readObj(reader.readInt()))
        this.totalSetting.WarningSE.readVersionedOui(reader.readObj(reader.readInt()))

        this.Title.readVersionedOui(reader.readObj(reader.readInt()))
        this.GameMenu.readVersionedOui(reader.readObj(reader.readInt()))
        this.CG.readVersionedOui(reader.readObj(reader.readInt()), dataVersion)
        this.BGM.readVersionedOui(reader.readObj(reader.readInt()), dataVersion)
        this.Sldata.readVersionedOui(reader.readObj(reader.readInt()))
        this.Talk.readVersionedOui(reader.readObj(reader.readInt()))
        this.backPlay.readVersionedOui(reader.readObj(reader.readInt()))
        this.SystemMenu.readVersionedOui(reader.readObj(reader.readInt()), dataVersion)
        this.btnList.readVersionedOui(reader.readObj(reader.readInt()))

        this.totalSetting.Init_savedata = reader.readInt() ? true : false

        this.customUI.readVersionedOui(reader.readObj(reader.readInt()))

        this.totalSetting.MenuIndex = reader.readInt()
        if (dataVersion >= 113) {
            this.totalSetting.MenuCodeSceneName = reader.readString()
        }

        this.bubblePersion.readVersionedOui(reader.readObj(reader.readInt()))
        // this.floatButton.readVersionedOui(reader.readObj(reader.readInt()))
    }

    writeVersionedOui(writer: FileWriter, dataVersion: number): void {
        writer.writerIntLength()

        writer.writeString(this.totalSetting.FontName)
        writer.writeInt(this.totalSetting.FontSize)
        writer.writeString(this.totalSetting.FontColor_MSG)
        writer.writeString(this.totalSetting.FontColor_UI)
        writer.writeInt(this.totalSetting.FontStyle)
        writer.writeInt(this.totalSetting.IsTitle ? 1 : 0)
        writer.writeInt(this.totalSetting.StartMapId)
        this.totalSetting.Ico.writeVersionedOui(writer)
        writer.writeInt(this.totalSetting.Show_ad)
        writer.writeString(this.totalSetting.AuthorWords)
        writer.writeInt(this.totalSetting.AuthorWordTime)

        writer.writeInt(this.SystemMenu.IsAutoText ? 1 : 0)
        writer.writeInt(this.IsShowSystemMenu ? 1 : 0)

        this.totalSetting.EnterSE.writeVersionedOui(writer)
        this.totalSetting.MoveSE.writeVersionedOui(writer)
        this.totalSetting.CancelSE.writeVersionedOui(writer)
        this.totalSetting.WarningSE.writeVersionedOui(writer)

        this.Title.writeVersionedOui(writer)
        this.GameMenu.writeVersionedOui(writer)
        this.CG.writeVersionedOui(writer, dataVersion)
        this.BGM.writeVersionedOui(writer, dataVersion)
        this.Sldata.writeVersionedOui(writer)
        this.Talk.writeVersionedOui(writer)
        this.backPlay.writeVersionedOui(writer)
        this.SystemMenu.writeVersionedOui(writer, dataVersion)
        this.btnList.writeVersionedOui(writer)

        writer.writeInt(this.totalSetting.Init_savedata ? 1 : 0)

        this.customUI.writeVersionedOui(writer)

        writer.writeInt(this.totalSetting.MenuIndex)
        writer.writeString(this.totalSetting.MenuCodeSceneName)

        this.bubblePersion.writeVersionedOui(writer)
        // this.floatButton.writeVersionedOui(writer)

        writer.writerLength()
    }

    writeGameBin(writer: FileWriter, runtimeFontName?: string): void {
        writer.writerIntLength()

        this.totalSetting.writeGameBin(writer, {
            engineVersion: DataUtils.engine_version,
            runtimeFontName,
            isShowSystemMenu: this.IsShowSystemMenu,
            section: 'head',
        })

        this.Title.writeGameBin(writer)
        this.GameMenu.writeGameBin(writer)
        this.CG.writeGameBin(writer, DataUtils.engine_version)
        this.BGM.writeGameBin(writer, DataUtils.engine_version)
        this.Sldata.writeGameBin(writer)
        this.Talk.writeGameBin(writer)
        this.backPlay.writeGameBin(writer)
        this.SystemMenu.writeGameBin(writer, DataUtils.engine_version)
        this.btnList.writeGameBin(writer)
        writer.writeInt(this.totalSetting.Init_savedata ? 1 : 0)
        if (DataUtils.engine_version >= 103) {
            this.customUI.writeGameBin(writer)
        }
        writer.writeInt(this.totalSetting.MenuIndex)
        if (DataUtils.engine_version >= 109) {
            writer.writeString(this.totalSetting.MenuCodeSceneName)
        }

        this.bubblePersion.writeGameBin(writer, DataUtils.engine_version, this.totalSetting.FontSettingList)
        writer.writerLength()
    }
}

export default SystemData
